import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface TierPlanEditorDialogProps {
    open: boolean;
    saving: boolean;
    stripePriceId?: string | null;
    editCurrency: string;
    currencySymbol: string;
    name: string;
    description: string;
    unitAmount: string;
    modelsAllowedEnabled: boolean;
    modelsAllowed: string;
    trialSupported: boolean;
    trialDuration: string;
    onClose: () => void;
    onSave: () => void;
    onNameChange: (value: string) => void;
    onDescriptionChange: (value: string) => void;
    onUnitAmountChange: (value: string) => void;
    onModelsAllowedEnabledChange: (value: boolean) => void;
    onModelsAllowedChange: (value: string) => void;
    onTrialSupportedChange: (value: boolean) => void;
    onTrialDurationChange: (value: string) => void;
}

const dialogTextFieldSx = {
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--button-background-color)',
        },
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: 'var(--button-background-color)',
    },
};

export function TierPlanEditorDialog({
    open,
    saving,
    stripePriceId,
    editCurrency,
    currencySymbol,
    name,
    description,
    unitAmount,
    modelsAllowedEnabled,
    modelsAllowed,
    trialSupported,
    trialDuration,
    onClose,
    onSave,
    onNameChange,
    onDescriptionChange,
    onUnitAmountChange,
    onModelsAllowedEnabledChange,
    onModelsAllowedChange,
    onTrialSupportedChange,
    onTrialDurationChange,
}: TierPlanEditorDialogProps) {
    const { t } = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 2.5,
                    border: '1px solid var(--selected-table-row-color)',
                    overflow: 'hidden',
                },
            }}
        >
            <DialogTitle sx={{ px: 3, py: 1.75, borderBottom: '1px solid var(--selected-table-row-color)', backgroundColor: 'action.hover' }}>
                <Stack spacing={0.5}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('TIER_MANAGEMENT_EDIT_TITLE')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'var(--link-text-color)' }}>
                        {stripePriceId}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ pt: '14px !important' }}>
                <Stack spacing={2.2} sx={{ mt: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--link-text-color)' }}>
                        {t('TIER_MANAGEMENT_NAME')} & {t('TIER_MANAGEMENT_DESCRIPTION')}
                    </Typography>
                    <TextField
                        label={t('TIER_MANAGEMENT_NAME')}
                        value={name}
                        onChange={(event) => onNameChange(event.target.value)}
                        fullWidth
                        required
                        disabled={saving}
                        sx={dialogTextFieldSx}
                        helperText={t('TIER_MANAGEMENT_NAME_HELPER')}
                    />
                    <TextField
                        label={t('TIER_MANAGEMENT_DESCRIPTION')}
                        value={description}
                        onChange={(event) => onDescriptionChange(event.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        disabled={saving}
                        sx={dialogTextFieldSx}
                        helperText={t('TIER_MANAGEMENT_DESCRIPTION_HELPER')}
                    />

                    <Divider sx={{ borderColor: 'var(--selected-table-row-color)' }} />

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--link-text-color)' }}>
                        {t('TIER_MANAGEMENT_PRICE')} & {t('USER_MANAGEMENT_MODELS_ALLOWED_LABEL')}
                    </Typography>
                    <TextField
                        label={`${t('TIER_MANAGEMENT_PRICE')}${editCurrency ? ` (${editCurrency})` : ''}`}
                        value={unitAmount}
                        onChange={(event) => onUnitAmountChange(event.target.value)}
                        fullWidth
                        required
                        disabled={saving}
                        sx={dialogTextFieldSx}
                        inputProps={{
                            inputMode: 'decimal',
                            min: 0,
                            step: '0.01',
                            pattern: '\\d+([.,]\\d{1,2})?',
                        }}
                        InputProps={{
                            startAdornment: currencySymbol ? (
                                <InputAdornment position="start">{currencySymbol}</InputAdornment>
                            ) : undefined,
                        }}
                        helperText={t('TIER_MANAGEMENT_PRICE_HELPER')}
                    />

                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={modelsAllowedEnabled}
                                onChange={(event) => onModelsAllowedEnabledChange(event.target.checked)}
                                disabled={saving}
                            />
                        )}
                        label={t('TIER_MANAGEMENT_MODELS_ALLOWED_LIMIT_LABEL')}
                    />
                    <TextField
                        label={t('USER_MANAGEMENT_MODELS_ALLOWED_LABEL')}
                        value={modelsAllowed}
                        onChange={(event) => onModelsAllowedChange(event.target.value)}
                        fullWidth
                        disabled={saving || !modelsAllowedEnabled}
                        sx={dialogTextFieldSx}
                        inputProps={{
                            inputMode: 'numeric',
                            min: 0,
                            step: '1',
                            pattern: '\\d*',
                        }}
                        helperText={modelsAllowedEnabled
                            ? t('TIER_MANAGEMENT_MODELS_ALLOWED_HELPER')
                            : t('SUBSCRIPTION_MODELS_ALLOWED_UNLIMITED')}
                    />

                    <Divider sx={{ borderColor: 'var(--selected-table-row-color)' }} />

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--link-text-color)' }}>
                        {t('TIER_MANAGEMENT_TRIAL_SUPPORTED')}
                    </Typography>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={trialSupported}
                                onChange={(event) => onTrialSupportedChange(event.target.checked)}
                                disabled={saving}
                            />
                        )}
                        label={t('TIER_MANAGEMENT_TRIAL_SUPPORTED_LABEL')}
                    />

                    {trialSupported && (
                        <TextField
                            select
                            label={t('TIER_MANAGEMENT_TRIAL_DURATION')}
                            value={trialDuration}
                            onChange={(event) => onTrialDurationChange(event.target.value)}
                            fullWidth
                            disabled={saving}
                            sx={dialogTextFieldSx}
                            helperText={t('TIER_MANAGEMENT_TRIAL_DURATION_HELPER')}
                        >
                            <MenuItem value="1_week">{t('TIER_MANAGEMENT_TRIAL_DURATION_1_WEEK')}</MenuItem>
                            <MenuItem value="2_weeks">{t('TIER_MANAGEMENT_TRIAL_DURATION_2_WEEKS')}</MenuItem>
                            <MenuItem value="1_month">{t('TIER_MANAGEMENT_TRIAL_DURATION_1_MONTH')}</MenuItem>
                        </TextField>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
                <Button
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                        color: 'var(--link-text-color)',
                        borderColor: 'var(--selected-table-row-color)',
                        minWidth: 110,
                        textTransform: 'none',
                    }}
                    variant="outlined"
                >
                    {t('CANCEL_BUTTON')}
                </Button>
                <Button
                    variant="contained"
                    onClick={onSave}
                    disabled={saving}
                    className="button-color"
                    sx={{ minWidth: 140, fontWeight: 700, textTransform: 'none' }}
                >
                    {saving ? <CircularProgress size={20} color="inherit" /> : t('SUBMIT_BUTTON')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
